import i18n from "src/locales";
import { PaymentAddonState } from "src/utils/redux/slices/paymentAddon";
import { getMeristemConfigValue } from "./meristemConfig";
import { isCheckoutTimerExpired } from "@utils/checkoutTimer";
import {
  isDECoursePackEligible,
  shouldShowMoodBundleCheckoutOffer,
  shouldShowNoomClinicalCheckoutOffer,
} from "./userSegment/features";
import { trackEvent } from "./api/tracker";
import { isAppIosBase } from "./userSegment";
import getStore from "./redux/store";

/* The utility functions in this file allow us to make changes to the course pack through the meristemConfig json
in order to enable "pseudo experiments" without consuming traffic. These pseudoexperiments can target any context in Meristem, and
the meristemConfig flags will propogate through the entire user journey to enable these course pack changes.

You will need to set up the meristemConfig to look something like this:

Control branch:

{
  "coursePackExperimentName": "exXXX",
  "coursePackExperimentVariation": "control"
}

Variation Branch:

{
  "coursePackExperimentName": "exXXX",
  "coursePackExperimentVariation": "variation",
  "coursePackProductId": "nw-plus-xxx", // New addon product id that will be used in the experiment
  "coursePackProductTitle": "Some title" // New addon product title that will be used in the experiment for contexts before payment-checkout where the add-on is not loaded yet
}

*/

export const DEFAULT_PRODUCT_ID = "nw-plus-ex97-baseline";
const DEFAULT_CHANNEL_ID = "hw-course-add-ons-baseline";
const DEFAULT_CLINICAL_CHANNEL_ID = "noom-clinical-baseline";
export const FREE_COURSE_PACK_OFFER_PRODUCT_ID = "nw-plus-free-baseline";

export const getCoursePackOfferChannel = () => {
  if (shouldShowNoomClinicalCheckoutOffer()) {
    return DEFAULT_CLINICAL_CHANNEL_ID;
  }

  return DEFAULT_CHANNEL_ID;
};

const getNoomClinicalProductId = () => {
  const { recommendedPlan } = getStore().getState();

  const planLength = recommendedPlan.duration_in_months;
  const trialDuration = recommendedPlan.trial_duration;

  const trialKeyMapping = {
    0: 0,
    7: 8,
    14: 15,
  };

  const trialKey = trialKeyMapping[trialDuration];
  if (trialKey === undefined) {
    trackEvent("NCInvalidBaseTrialDuration", { trialDuration });
    return "";
  }

  return `telehealth-access-self-pay-subscription-${planLength}m-${trialKey}t`;
};

export const getCoursePackOfferId = () => {
  if (shouldShowNoomClinicalCheckoutOffer()) {
    return getNoomClinicalProductId();
  }

  if (isDECoursePackEligible()) {
    return `${DEFAULT_PRODUCT_ID}-de`;
  }

  const productId: string =
    getMeristemConfigValue("coursePackProductId") ||
    FREE_COURSE_PACK_OFFER_PRODUCT_ID;

  if (productId === FREE_COURSE_PACK_OFFER_PRODUCT_ID) {
    return isCheckoutTimerExpired()
      ? DEFAULT_PRODUCT_ID
      : FREE_COURSE_PACK_OFFER_PRODUCT_ID;
  }

  return productId;
};

export const getCoursePackTitle = (
  paymentAddon?: Partial<PaymentAddonState>
) => {
  if (paymentAddon?.title) {
    return paymentAddon.title;
  }
  // Allow using a title from meristem config in case we need it earlier in the buyflow
  // when the paymentAddon is not loaded yet and we don't want to make an extra API call
  const coursePackProductTitle = getMeristemConfigValue(
    "coursePackProductTitle"
  );
  return coursePackProductTitle || i18n.t("price-breakdown:coursePackTitle");
};

// By default this is called from the checkout page, but if any changes are made earlier then the call to this function will need to be moved
export const maybeTrackCoursePackExperimentStarted = () => {
  const coursePackExperimentName = getMeristemConfigValue(
    "coursePackExperimentName"
  );

  const coursePackExperimentVariation = getMeristemConfigValue(
    "coursePackExperimentVariation"
  );

  if (
    coursePackExperimentName &&
    coursePackExperimentVariation &&
    shouldShowMoodBundleCheckoutOffer()
  ) {
    trackEvent("CoursePackHoldoutExperimentStarted", {
      experimentName: coursePackExperimentName,
      experimentVariation: coursePackExperimentVariation,
    });
  }
};

export function getCoursePackTextPrefix(basePrefix) {
  // Note (Pratik) German and the inAppIOS route should
  // receive the original experience
  if (isDECoursePackEligible() || isAppIosBase()) {
    return basePrefix;
  }

  if (isNewBaselineCoursePackVariant()) {
    return `${basePrefix}WithNMCoursepack`;
  }

  return basePrefix;
}

// Note (Pratik) if the meristem config value is undefined we should show the baseline
export function isNewBaselineCoursePackVariant() {
  return getMeristemConfigValue("coursePackExperimentVariation") !== "control";
}
